import React, { PropsWithChildren, createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

type Listener = () => unknown;

interface Payload {
  onClose: (callback: Listener) => void;
  removeListener: (callback: Listener) => void;
}

const closableContext = createContext<Payload | null>(null);

export default function useOnParentClose(onClose?: Listener) {
  const parent = useContext(closableContext);
  useEffect(() => {
    if (!parent || !onClose) return;
    parent.onClose(onClose);
    return () => parent.removeListener(onClose);
  }, [parent, onClose]);
  return parent;
}

export function Closable({ children, isOpen }: PropsWithChildren<{ isOpen: boolean }>) {
  const [listeners, setListeners] = useState<Listener[]>([]);

  const close = useCallback(() => {
    for (const listener of listeners) listener();
  }, [listeners]);

  useEffect(() => {
    if (!isOpen) close();
  }, [isOpen, close]);

  useOnParentClose(close);

  const payload = useMemo<Payload>(
    () => ({
      onClose(listener: Listener) {
        setListeners((listeners) => [...listeners, listener]);
      },
      removeListener(listener: Listener) {
        setListeners((listeners) => listeners.filter((l) => l !== listener));
      },
    }),
    [],
  );

  return <closableContext.Provider value={payload}>{children}</closableContext.Provider>;
}
